import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Modal, ScrollView, Pressable, Alert, Platform, KeyboardAvoidingView, Dimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import { Calendar } from '@/components/ui/Calendar';
import { TimePickerInput } from '@/components/ui/TimePickerInput';
import type { ItineraryItem, ItineraryItemCategory } from '@/services/itinerary';
import type { TripDestination } from '@/services/trips';
import { uploadItineraryDocument } from '@/services/documentUpload';
import { normalizeAttachments, type ItineraryAttachment } from '@/services/itineraryAttachments';
import { AutocompleteInput } from '@/components/ui/AutocompleteInput';
import { getLocationSuggestions } from '@/utils/formSuggestions';
import { searchPlaces } from '@/services/maps';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { getDestinationsForDate } from '@/utils/destinationDate';
import { coerceDate } from '@/utils/dateKeys';
import * as Linking from 'expo-linking';

type ItineraryItemFormProps = {
  visible: boolean;
  onClose: () => void;
  onSubmit: (item: Omit<ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  tripId: string;
  userId: string;
  initialItem?: ItineraryItem;
  defaultDate?: Date;
  tripStartDate: Date;
  tripEndDate: Date;
  tripDestinations?: TripDestination[];
};

const CATEGORY_OPTIONS: SelectOption[] = [
  { value: 'flight', label: 'Flight' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'activity', label: 'Activity' },
  { value: 'train', label: 'Train' },
  { value: 'ferry', label: 'Ferry / boat' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'car', label: 'Car' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'planned', label: 'Planned' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatTime = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const parseTime = (timeString: string): Date => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes || 0, 0, 0);
  return date;
};

/**
 * Round to next 5-minute interval
 */
const nextFiveMinutes = (): string => {
  const now = new Date();
  const minutes = now.getMinutes();
  const rounded = Math.ceil(minutes / 5) * 5;
  const next = new Date(now);
  next.setSeconds(0, 0);
  if (rounded >= 60) {
    next.setHours(now.getHours() + 1);
    next.setMinutes(0);
  } else {
    next.setMinutes(rounded);
  }
  return `${next.getHours().toString().padStart(2, '0')}:${next.getMinutes().toString().padStart(2, '0')}`;
};

export function ItineraryItemForm({
  visible,
  onClose,
  onSubmit,
  tripId,
  userId,
  initialItem,
  defaultDate,
  tripStartDate,
  tripEndDate,
  tripDestinations,
}: ItineraryItemFormProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { height: screenHeight } = Dimensions.get('window');
  const sheetMaxHeight = Math.min(screenHeight * 0.95, screenHeight - insets.top - 16);
  const sheetMinHeight = Math.max(screenHeight * 0.75, 520);
  const safeTripStartDate = useMemo(
    () => coerceDate(tripStartDate) ?? new Date(),
    [tripStartDate],
  );
  const safeTripEndDate = useMemo(
    () => coerceDate(tripEndDate) ?? safeTripStartDate,
    [tripEndDate, safeTripStartDate],
  );
  const safeDefaultDate = useMemo(
    () => coerceDate(defaultDate) ?? safeTripStartDate,
    [defaultDate, safeTripStartDate],
  );
  const safeInitialStartDate = useMemo(
    () => coerceDate(initialItem?.startDate) ?? safeDefaultDate,
    [initialItem?.startDate, safeDefaultDate],
  );
  const safeInitialEndDate = useMemo(
    () => coerceDate(initialItem?.endDate),
    [initialItem?.endDate],
  );
  const [title, setTitle] = useState(initialItem?.title || '');
  const [description, setDescription] = useState(initialItem?.description || '');
  const [category, setCategory] = useState<ItineraryItemCategory>(
    initialItem?.category || 'other'
  );
  const [startDate, setStartDate] = useState<Date>(safeInitialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(safeInitialEndDate);
  const [startTime, setStartTime] = useState<string>(
    initialItem?.startTime === 'flexible' ? '' : (initialItem?.startTime || '')
  );
  const [endTime, setEndTime] = useState<string>(initialItem?.endTime || '');
  const [cityIndex, setCityIndex] = useState<number | null>(initialItem?.cityIndex ?? null);
  const [isFlexible, setIsFlexible] = useState<boolean>(
    !initialItem?.startTime || initialItem?.startTime === 'flexible'
  );
  const [address, setAddress] = useState(initialItem?.address || '');
  const [location, setLocation] = useState(initialItem?.location || '');
  const [attachments, setAttachments] = useState<ItineraryAttachment[]>(
    normalizeAttachments(initialItem?.attachments, initialItem?.documents || [])
  );
  // Stable upload id for this form instance. New items can upload before Firestore item exists.
  const uploadItemIdRef = useRef<string>(
    initialItem?.id || `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );

  /**
   * Identity key for the item being edited — NOT `initialItem` by reference. Parents often pass a
   * fresh object every render (e.g. inline props), and `defaultDate` is often a new `Date` each
   * render, which used to retrigger a full form reset and clear in-progress attachments.
   */
  const initialItemSyncKey = useMemo(() => {
    if (!initialItem) return 'new';
    if (initialItem.id) return `id:${initialItem.id}`;
    const t = coerceDate(initialItem.startDate)?.getTime() ?? 0;
    const title = (initialItem.title || '').slice(0, 80);
    const loc = (initialItem.location || initialItem.address || '').slice(0, 120);
    return `anon:${title}|${t}|${loc}`;
  }, [
    initialItem?.id,
    initialItem?.title,
    initialItem ? (coerceDate(initialItem.startDate)?.getTime() ?? 0) : -1,
    initialItem?.location,
    initialItem?.address,
  ]);

  const lastAppliedItemSyncKeyRef = useRef<string | null>(null);

  // Reset form from props only when the sheet opens, or when the user edits a different item
  // (see initialItemSyncKey). Do not reset on every parent re-render.
  useEffect(() => {
    if (!visible) {
      lastAppliedItemSyncKeyRef.current = null;
      return;
    }
    if (lastAppliedItemSyncKeyRef.current === initialItemSyncKey) {
      return;
    }
    lastAppliedItemSyncKeyRef.current = initialItemSyncKey;

    if (initialItem) {
      setTitle(initialItem.title || '');
      setDescription(initialItem.description || '');
      setCategory(initialItem.category || 'other');
      setStartDate(coerceDate(initialItem.startDate) ?? safeDefaultDate);
      setEndDate(coerceDate(initialItem.endDate));
      setStartTime(initialItem.startTime === 'flexible' ? '' : (initialItem.startTime || ''));
      setEndTime(initialItem.endTime || '');
      setIsFlexible(!initialItem.startTime || initialItem.startTime === 'flexible');
      setCityIndex(initialItem.cityIndex ?? null);
      setAddress(initialItem.address || '');
      setLocation(initialItem.location || '');
      setAirlineCode(initialItem.airlineCode || '');
      setFlightNumber(initialItem.flightNumber || '');
      setStatus(initialItem.status || 'planned');
      setAttachments(normalizeAttachments(initialItem.attachments, initialItem.documents || []));
      if (initialItem.id) {
        uploadItemIdRef.current = initialItem.id;
      }
    } else {
      // Reset flight fields when adding new item
      setAirlineCode('');
      setFlightNumber('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialItem + safeDefaultDate omitted on purpose: reset is keyed by `initialItemSyncKey` and `visible` so parent re-renders do not wipe in-progress attachments
  }, [visible, initialItemSyncKey]);
  const [status, setStatus] = useState(initialItem?.status || 'planned');
  const [airlineCode, setAirlineCode] = useState(initialItem?.airlineCode || '');
  const [flightNumber, setFlightNumber] = useState(initialItem?.flightNumber || '');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const destinationCandidates = useMemo(() => {
    if (!tripDestinations || tripDestinations.length < 2) return [];
    if (!startDate) return [];
    return getDestinationsForDate(tripDestinations, startDate);
  }, [tripDestinations, startDate]);

  const showCitySelector = destinationCandidates.length > 1;

  const cityOptions: SelectOption[] = useMemo(() => {
    if (!showCitySelector) return [];
    return [
      { value: 'auto', label: 'Auto (by time/address)' },
      ...destinationCandidates.map((c) => ({
        value: String(c.index),
        label: c.destination.city || `City ${c.index + 1}`,
      })),
    ];
  }, [destinationCandidates, showCitySelector]);

  // If startDate changes and the previously selected cityIndex is no longer a candidate, reset to auto.
  useEffect(() => {
    if (!showCitySelector) return;
    if (cityIndex === null) return;
    const stillValid = destinationCandidates.some((c) => c.index === cityIndex);
    if (!stillValid) {
      setCityIndex(null);
    }
  }, [cityIndex, destinationCandidates, showCitySelector]);

  // Backwards-compatible documents list for legacy consumers
  const documents = Array.from(new Set(attachments.map(att => att.url).filter(Boolean)));

  const uploadPickedAssets = async (
    assets: Array<{ uri: string; name?: string | null; mimeType?: string | null }>
  ) => {
    setUploading(true);
    try {
      const UPLOAD_TIMEOUT_MS = 90_000;
      const withUploadTimeout = async <T,>(promise: Promise<T>): Promise<T> => {
        return await Promise.race([
          promise,
          new Promise<T>((_, reject) => {
            setTimeout(
              () => reject(new Error('Upload timed out. Try a smaller file or check your connection.')),
              UPLOAD_TIMEOUT_MS,
            );
          }),
        ]);
      };

      const uploadPromises = assets.map(async (asset, assetIndex) => {
        try {
          let uri = asset.uri;
          let mimeType = asset.mimeType || 'application/octet-stream';
          let logicalName = asset.name;

          if (Platform.OS !== 'web' && mimeType.startsWith('image/')) {
            try {
              const IM = await import('expo-image-manipulator');
              const out = await IM.manipulateAsync(
                uri,
                [{ resize: { width: 1920 } }],
                { compress: 0.82, format: IM.SaveFormat.JPEG },
              );
              uri = out.uri;
              mimeType = 'image/jpeg';
              if (logicalName && !/\.jpe?g$/i.test(logicalName)) {
                logicalName = `${logicalName.replace(/\.[^./]+$/, '')}.jpg`;
              }
            } catch {
              // Keep original image if manipulation fails
            }
          }

          const response = await fetch(uri);
          const blob = await response.blob();
          const fallbackExtension =
            mimeType.includes('pdf')
              ? 'pdf'
              : mimeType.includes('png')
                ? 'png'
                : mimeType.includes('jpeg') || mimeType.includes('jpg')
                  ? 'jpg'
                  : 'bin';
          const fileName = logicalName || `attachment-${Date.now()}-${assetIndex + 1}.${fallbackExtension}`;

          const itemIdForUpload = uploadItemIdRef.current;
          const uploadResult =
            Platform.OS === 'web'
              ? await withUploadTimeout(
                  uploadItineraryDocument(
                    tripId,
                    itemIdForUpload,
                    new File([blob], fileName, { type: mimeType || 'application/octet-stream' }),
                  ),
                )
              : await withUploadTimeout(
                  uploadItineraryDocument(tripId, itemIdForUpload, blob, {
                    fileName,
                    contentType: mimeType || 'application/octet-stream',
                  }),
                );

          if (!uploadResult.success) {
            throw new Error(uploadResult.error || 'Upload failed');
          }

          if (uploadResult.attachment) {
            return {
              ok: true as const,
              attachment: {
                ...uploadResult.attachment,
                // Do not persist picker temp URIs as offline copies.
                // Real offline attachment paths are managed by itinerary attachment caching services.
                offlinePath: null,
              } as ItineraryAttachment,
            };
          }
          if (uploadResult.url) {
            return {
              ok: true as const,
              attachment: {
                id: `${Date.now()}-${fileName}`,
                name: fileName,
                url: uploadResult.url,
                contentType: asset.mimeType || null,
                size: blob.size,
                source: 'upload' as const,
                createdAt: new Date(),
                // Do not persist picker temp URIs as offline copies.
                offlinePath: null,
              } as ItineraryAttachment,
            };
          }
          throw new Error('Upload succeeded but no URL returned');
        } catch (e: any) {
          const name = asset.name || 'document';
          return { ok: false as const, fileName: name, error: e?.message || 'Upload failed' };
        }
      });

      const results = await Promise.all(uploadPromises);
      const uploadedAttachments = results
        .filter((r): r is { ok: true; attachment: ItineraryAttachment } => !!r && (r as any).ok === true)
        .map((r) => r.attachment);
      const failures = results.filter((r): r is { ok: false; fileName: string; error: string } => !!r && (r as any).ok === false);

      if (uploadedAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...uploadedAttachments]);
      }
      if (failures.length > 0) {
        const first = failures[0];
        Alert.alert(
          'Upload failed',
          failures.length === 1
            ? `${first.fileName}: ${first.error}`
            : `${failures.length} files failed. First: ${first.fileName}: ${first.error}`
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const handlePickDocuments = async () => {
    try {
      // Try to dynamically import expo-document-picker
      let DocumentPicker;
      try {
        DocumentPicker = await import('expo-document-picker');
      } catch (e) {
        Alert.alert(
          'Document Picker Not Available',
          'Please install expo-document-picker: npm install expo-document-picker'
        );
        return;
      }

      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/*', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        multiple: true,
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets) {
        await uploadPickedAssets(result.assets);
      }
    } catch (error) {
      console.error('Error picking documents:', error);
      Alert.alert('Error', 'Failed to pick documents');
    }
  };

  const handlePickPhotos = async () => {
    try {
      const ImagePicker = await import('expo-image-picker');
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (permission.status !== 'granted') {
        Alert.alert(
          'Permission needed',
          'Allow photo library access to attach images. You can continue without attachments.',
          [
            { text: 'Not now', style: 'cancel' },
            { text: 'Open Settings', onPress: () => void Linking.openSettings() },
          ],
        );
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.85,
      });

      if (!result.canceled && result.assets) {
        await uploadPickedAssets(
          result.assets.map((asset, index) => ({
            uri: asset.uri,
            mimeType: asset.mimeType || 'image/jpeg',
            name: asset.fileName || `photo-${Date.now()}-${index + 1}.jpg`,
          }))
        );
      }
    } catch (error) {
      console.error('Error picking photos:', error);
      Alert.alert('Error', 'Failed to pick photos');
    }
  };

  const handleAddAttachments = () => {
    if (Platform.OS === 'web') {
      void handlePickDocuments();
      return;
    }

    Alert.alert('Add attachment', 'Choose where to upload from.', [
      { text: 'Photos', onPress: () => { void handlePickPhotos(); } },
      { text: 'Files', onPress: () => { void handlePickDocuments(); } },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    }

    // Normalize dates to compare only date portion (ignore time)
    const normalizeDate = (date: Date): Date => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };

    if (startDate) {
      const normalizedStartDate = normalizeDate(startDate);
      const normalizedTripStart = normalizeDate(safeTripStartDate);
      const normalizedTripEnd = normalizeDate(safeTripEndDate);

      // Check if start date is within trip range
      if (normalizedStartDate < normalizedTripStart || normalizedStartDate > normalizedTripEnd) {
        newErrors.startDate = `Start date must be between ${safeTripStartDate.toLocaleDateString()} and ${safeTripEndDate.toLocaleDateString()}`;
      }

      if (endDate) {
        const normalizedEndDate = normalizeDate(endDate);
        
        // Check if end date is within trip range
        if (normalizedEndDate < normalizedTripStart || normalizedEndDate > normalizedTripEnd) {
          newErrors.endDate = `End date must be between ${safeTripStartDate.toLocaleDateString()} and ${safeTripEndDate.toLocaleDateString()}`;
        }

        // Check if end date is after start date
        if (normalizedEndDate < normalizedStartDate) {
          newErrors.endDate = 'End date must be after start date';
        }
      }
    }

    if (category === 'activity' && !isFlexible && !startTime.trim()) {
      newErrors.startTime = 'Start time is required for activities unless marked flexible';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      const normalizedAirlineCode =
        category === 'flight' && airlineCode.trim()
          ? airlineCode.trim().toUpperCase()
          : undefined;
      const normalizedFlightNumber =
        category === 'flight' && flightNumber.trim()
          ? flightNumber.trim().toUpperCase()
          : undefined;

      const item: Omit<ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'> = {
        title: title.trim(),
        description: description.trim() || undefined,
        category,
        startDate,
        endDate: endDate || undefined,
        startTime: isFlexible ? 'flexible' : (startTime.trim() || undefined),
        endTime: endTime.trim() || undefined,
        cityIndex: showCitySelector ? cityIndex : (initialItem?.cityIndex ?? cityIndex),
        address: address.trim() || undefined,
        location: location.trim() || undefined,
        airlineCode: normalizedAirlineCode,
        flightNumber: normalizedFlightNumber,
        bookingId: initialItem?.bookingId,
        source: initialItem?.source || 'manual',
        documents,
        attachments,
        status: status as ItineraryItem['status'],
        createdBy: userId,
      };

      const SUBMIT_TIMEOUT_MS = 35_000;
      const SUBMIT_GRACE_MS = 15_000;
      const submitPromise = Promise.resolve(onSubmit(item));
      const outcomePromise = submitPromise.then(
        () => ({ status: 'success' as const }),
        (submitError: unknown) => ({ status: 'error' as const, submitError }),
      );
      type SubmitRaceOutcome =
        | { status: 'success' }
        | { status: 'error'; submitError: unknown }
        | { status: 'timeout' };
      const submitRace = await Promise.race<SubmitRaceOutcome>([
        outcomePromise,
        new Promise<SubmitRaceOutcome>((resolve) => {
          setTimeout(() => resolve({ status: 'timeout' }), SUBMIT_TIMEOUT_MS);
        }),
      ]);

      if (submitRace.status === 'error') {
        throw submitRace.submitError;
      }

      if (submitRace.status === 'timeout') {
        type PostTimeoutOutcome =
          | { status: 'success' }
          | { status: 'error'; submitError: unknown }
          | { status: 'still_pending' };
        const postTimeoutRace = await Promise.race<PostTimeoutOutcome>([
          outcomePromise,
          new Promise<PostTimeoutOutcome>((resolve) => {
            setTimeout(() => resolve({ status: 'still_pending' }), SUBMIT_GRACE_MS);
          }),
        ]);

        if (postTimeoutRace.status === 'error') {
          void logAnalyticsEvent('itinerary_save_timeout', {
            tripId,
            category,
            isEdit: Boolean(initialItem),
            outcome: 'failed_after_timeout',
          });
          throw postTimeoutRace.submitError;
        }

        if (postTimeoutRace.status === 'still_pending') {
          void logAnalyticsEvent('itinerary_save_timeout', {
            tripId,
            category,
            isEdit: Boolean(initialItem),
            outcome: 'still_pending',
          });
          Alert.alert(
            'Still saving',
            'Saving is taking longer than expected. Your itinerary item may still be processing in the background. Check your itinerary before trying again.'
          );
          setSubmitting(false);
          return;
        }

        void logAnalyticsEvent('itinerary_save_timeout', {
          tripId,
          category,
          isEdit: Boolean(initialItem),
          outcome: 'resolved_during_grace',
        });
      }
      // Form will be closed by parent component after successful submission
      setSubmitting(false);
      setErrors({});
    } catch (error) {
      console.error('Error submitting itinerary item:', error);
      Alert.alert(
        'Error', 
        error instanceof Error ? error.message : 'Failed to save itinerary item'
      );
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle(initialItem?.title || '');
    setDescription(initialItem?.description || '');
    setCategory(initialItem?.category || 'other');
    setStartDate(initialItem?.startDate || defaultDate || new Date());
    setEndDate(initialItem?.endDate || null);
    setStartTime(initialItem?.startTime === 'flexible' ? '' : (initialItem?.startTime || ''));
    setEndTime(initialItem?.endTime || '');
    setIsFlexible(!initialItem?.startTime || initialItem?.startTime === 'flexible');
    setCityIndex(initialItem?.cityIndex ?? null);
    setAddress(initialItem?.address || '');
    setLocation(initialItem?.location || '');
    setStatus(initialItem?.status || 'planned');
    setAirlineCode(initialItem?.airlineCode || '');
    setFlightNumber(initialItem?.flightNumber || '');
    // Documents are derived from attachments; reset attachments for a clean form state
    setAttachments(normalizeAttachments(initialItem?.attachments, initialItem?.documents || []));
    setErrors({});
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={handleClose}
      presentationStyle="overFullScreen"
      accessibilityViewIsModal={true}
      accessibilityLabel={initialItem ? 'Edit itinerary item' : 'Add itinerary item'}
    >
      <SelectFieldModalProvider>
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.overlay,
          justifyContent: 'flex-end',
        }}
      >
        <Pressable 
          style={{ flex: 1 }} 
          onPress={handleClose}
          accessibilityLabel="Close modal background"
          accessibilityRole="button"
        />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'height' : undefined}
          keyboardVerticalOffset={0}
        >
          {/* Bottom sheet container */}
          <View
            accessibilityRole="none"
            accessibilityLabel={initialItem ? 'Edit itinerary item form' : 'Add itinerary item form'}
            style={{
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
              maxHeight: sheetMaxHeight,
              minHeight: sheetMinHeight,
              paddingBottom: insets.bottom + theme.spacing(1),
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: theme.spacing(2),
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.borderMuted,
              }}
            >
              <SubHeading>{initialItem ? 'Edit Item' : 'Add Itinerary Item'}</SubHeading>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Close form"
                accessibilityHint="Closes the form and discards any unsaved changes"
                onPress={handleClose}
                style={({ pressed }) => ({
                  padding: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </Pressable>
            </View>

            {/* Form */}
            <ScrollView
              style={{ flex: 1 }}
              showsVerticalScrollIndicator
              contentContainerStyle={{
                padding: theme.spacing(2),
                gap: theme.spacing(2),
                paddingBottom: theme.spacing(3) + insets.bottom,
              }}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
              automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
            >
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              Scroll to see all fields
            </Caption>
            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <View
                accessibilityRole="alert"
                accessibilityLiveRegion="assertive"
                style={{
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.error + '15',
                  borderRadius: theme.radii.md,
                  borderWidth: 1,
                  borderColor: theme.colors.error + '40',
                  gap: theme.spacing(0.5),
                }}
              >
                <ThemedText variant="bodyStrong" color={theme.colors.error}>
                  Please fix the following errors:
                </ThemedText>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldLabels: Record<string, string> = {
                    title: 'Title',
                    startDate: 'Start date',
                    endDate: 'End date',
                  };
                  return (
                    <ThemedText key={field} variant="body" color={theme.colors.error}>
                      • {fieldLabels[field] || field}: {message}
                    </ThemedText>
                  );
                })}
              </View>
            )}

            <InputField
              label="Title"
              required
              value={title}
              onChangeText={(text) => {
                setTitle(text);
                if (errors.title) {
                  setErrors(prev => ({ ...prev, title: '' }));
                }
              }}
              placeholder="e.g., Flight to Paris"
              errorText={errors.title}
            />

            <SelectField
              label="Category"
              value={category}
              options={CATEGORY_OPTIONS}
              onValueChange={(value) => setCategory(value as ItineraryItemCategory)}
              avoidNestedModal
            />

            <InputField
              label="Description"
              value={description}
              onChangeText={setDescription}
              placeholder="Additional details..."
              multiline
              numberOfLines={3}
            />

            {category === 'flight' && (
              <Card variant="outlined">
                <View 
                  accessibilityRole="none"
                  accessibilityLabel="Flight details, optional"
                  style={{ gap: theme.spacing(1.5) }}
                >
                  <ThemedText variant="bodyStrong">Flight Details (optional)</ThemedText>
                  <InputField
                    label="Airline code"
                    value={airlineCode}
                    onChangeText={(text) => setAirlineCode(text.toUpperCase())}
                    placeholder="e.g., AA"
                    autoCapitalize="characters"
                    maxLength={3}
                    helperText="Two or three-letter airline designator"
                  />
                  <InputField
                    label="Flight number"
                    value={flightNumber}
                    onChangeText={(text) => setFlightNumber(text.toUpperCase())}
                    placeholder="e.g., 1234"
                    helperText="Digits or alphanumeric flight number"
                  />
                </View>
              </Card>
            )}

            {/* Date and Time */}
            <View 
              accessibilityRole="none"
              accessibilityLabel="Date and time"
              style={{ gap: theme.spacing(1.5) }}
            >
              <ThemedText variant="bodyStrong">Date & Time</ThemedText>
              
              {/* Prompt for activities */}
              {category === 'activity' && !isFlexible && !startTime && (
                <View
                  style={{
                    padding: theme.spacing(1.5),
                    backgroundColor: theme.colors.primary + '15',
                    borderRadius: theme.radii.md,
                    borderWidth: 1,
                    borderColor: theme.colors.primary + '40',
                  }}
                >
                  <ThemedText variant="body" style={{ fontSize: 13, color: theme.colors.primary }}>
                    💡 Add a time to help organize your day, or mark as flexible if timing isn't set yet.
                  </ThemedText>
                </View>
              )}
              
              <View style={{ gap: theme.spacing(0.5) }}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Start date, ${startDate.toLocaleDateString()}. Tap to change date`}
                  accessibilityHint="Opens calendar picker to select a new start date"
                  onPress={() => {
                    setShowStartDatePicker(true);
                    if (errors.startDate) {
                      setErrors(prev => ({ ...prev, startDate: '' }));
                    }
                  }}
                  style={({ pressed }) => [
                    {
                      borderWidth: 1,
                      borderColor: errors.startDate ? theme.colors.error : theme.colors.border,
                      borderRadius: theme.radii.md,
                      paddingHorizontal: theme.spacing(2),
                      paddingVertical: theme.spacing(1.5),
                      backgroundColor: theme.colors.surface,
                      opacity: pressed ? 0.7 : 1,
                      minHeight: 44,
                    },
                  ]}
                >
                  <ThemedText variant="body">
                    Start Date: {startDate.toLocaleDateString()}
                  </ThemedText>
                </Pressable>
                {errors.startDate && (
                  <Caption 
                    color={theme.colors.error} 
                    style={{ fontSize: 12 }}
                    accessibilityLiveRegion="polite"
                    accessibilityRole="alert"
                  >
                    {errors.startDate}
                  </Caption>
                )}
              </View>

              {/* Start Time with Flexible Option */}
              <View style={{ gap: theme.spacing(1) }}>
                {isFlexible ? (
                  <InputField
                    label={category === 'activity' ? 'Start Time *' : 'Start Time'}
                    value="Flexible"
                    editable={false}
                    style={{ opacity: 0.7 }}
                  />
                ) : (
                  <TimePickerInput
                    label={category === 'activity' ? 'Start Time *' : 'Start Time'}
                    placeholder="Enter time (e.g., 7:30 AM)"
                    value={startTime}
                    onValueChange={(value) => {
                      setStartTime(value);
                      if (value.trim()) setIsFlexible(false);
                      if (errors.startTime) setErrors(prev => ({ ...prev, startTime: '' }));
                    }}
                    allowClear={category !== 'activity'}
                    errorText={errors.startTime}
                    helperText={category === 'activity' ? 'Required unless Flexible is enabled' : undefined}
                  />
                )}
                
                {/* Flexible Option */}
                <Pressable
                  onPress={() => {
                    const newFlexible = !isFlexible;
                    setIsFlexible(newFlexible);
                    if (newFlexible) {
                      setStartTime('');
                      if (errors.startTime) setErrors(prev => ({ ...prev, startTime: '' }));
                    } else {
                      // Turning off Flexible should create a concrete time so it persists after saving.
                      if (!startTime.trim()) {
                        setStartTime(nextFiveMinutes());
                      }
                    }
                  }}
                  style={({ pressed }) => [
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.spacing(1),
                      padding: theme.spacing(1.5),
                      borderRadius: theme.radii.md,
                      borderWidth: 1,
                      borderColor: isFlexible ? theme.colors.primary : theme.colors.border,
                      backgroundColor: isFlexible ? theme.colors.primary + '15' : theme.colors.surface,
                      opacity: pressed ? 0.7 : 1,
                    },
                  ]}
                  accessibilityRole={Platform.OS === 'android' ? 'button' : 'checkbox'}
                  accessibilityState={{ checked: isFlexible }}
                  accessibilityLabel="Mark as flexible time"
                >
                  <Ionicons
                    name={isFlexible ? 'checkbox' : 'square-outline'}
                    size={20}
                    color={isFlexible ? theme.colors.primary : theme.colors.textSecondary}
                  />
                  <ThemedText variant="body" style={{ flex: 1 }}>
                    Flexible (no specific time planned)
                  </ThemedText>
                </Pressable>
              </View>

              {showCitySelector && (
                <Card variant="outlined">
                  <View style={{ gap: theme.spacing(1) }}>
                    <ThemedText variant="bodyStrong">City (overlap day)</ThemedText>
                    <Caption color={theme.colors.textSecondary}>
                      This day spans multiple destinations. Choose a city to keep pins and suggestions accurate, or leave on Auto.
                    </Caption>
                    <SelectField
                      label="City"
                      value={cityIndex === null ? 'auto' : String(cityIndex)}
                      options={cityOptions}
                      onValueChange={(value) => {
                        if (value === 'auto') {
                          setCityIndex(null);
                          return;
                        }
                        const parsed = Number(value);
                        setCityIndex(Number.isFinite(parsed) ? parsed : null);
                      }}
                      avoidNestedModal
                    />
                  </View>
                </Card>
              )}

              <View style={{ gap: theme.spacing(0.5) }}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`End date, ${endDate ? endDate.toLocaleDateString() : 'not set'}. Tap to change date`}
                  accessibilityHint="Opens calendar picker to select an end date. Optional field."
                  onPress={() => {
                    setShowEndDatePicker(true);
                    if (errors.endDate) {
                      setErrors(prev => ({ ...prev, endDate: '' }));
                    }
                  }}
                  style={({ pressed }) => [
                    {
                      borderWidth: 1,
                      borderColor: errors.endDate ? theme.colors.error : theme.colors.border,
                      borderRadius: theme.radii.md,
                      paddingHorizontal: theme.spacing(2),
                      paddingVertical: theme.spacing(1.5),
                      backgroundColor: theme.colors.surface,
                      opacity: pressed ? 0.7 : 1,
                      minHeight: 44,
                    },
                  ]}
                >
                  <ThemedText variant="body">
                    End Date: {endDate ? endDate.toLocaleDateString() : 'Optional'}
                  </ThemedText>
                </Pressable>
                {errors.endDate && (
                  <Caption 
                    color={theme.colors.error} 
                    style={{ fontSize: 12 }}
                    accessibilityLiveRegion="polite"
                    accessibilityRole="alert"
                  >
                    {errors.endDate}
                  </Caption>
                )}
              </View>

              <TimePickerInput
                label="End Time (optional)"
                placeholder="Enter time (e.g., 5:30 PM)"
                value={endTime}
                onValueChange={(value) => setEndTime(value)}
                allowClear
              />
            </View>

            {/* Location */}
            <AutocompleteInput
              label="Address"
              value={address}
              onChangeText={setAddress}
              placeholder="Street address"
              minQueryLength={4}
              showSuggestionsOnFocus={true}
              skipAsyncSuggestionsUntilEdited={Boolean(initialItem?.address?.trim())}
              baselineValueForSkip={initialItem?.address ?? ''}
              // Mapbox POI names often won't contain every word a user types (e.g. "train station"),
              // so we do token-based matching instead of requiring the full query substring.
              filterSuggestions={(normalizedQuery, suggestions) => {
                const tokens = normalizedQuery
                  .split(/\s+/)
                  .map(t => t.trim())
                  .filter(Boolean);

                const scored = suggestions
                  .map((s) => {
                    const haystack = `${s.label} ${s.value} ${s.subtitle ?? ''}`.toLowerCase();
                    const matchCount = tokens.reduce((acc, token) => acc + (haystack.includes(token) ? 1 : 0), 0);
                    return { s, matchCount, haystack };
                  })
                  .filter(({ matchCount, haystack }) => matchCount > 0 || haystack.includes(normalizedQuery));

                scored.sort((a, b) => {
                  if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;
                  return a.s.label.localeCompare(b.s.label);
                });

                return scored.map(x => x.s);
              }}
              suggestions={async (query) => {
                if (query.trim().length < 4) {
                  return [];
                }
                try {
                  const results = await searchPlaces(query, {
                    limit: 5,
                    types: ['address', 'poi'],
                    useCache: true,
                    autocomplete: false,
                  });
                  return results.map((r) => {
                    const [first, ...rest] = r.placeName.split(',').map((p) => p.trim()).filter(Boolean);
                    return {
                      value: r.placeName,
                      label: first || r.placeName,
                      subtitle: rest.join(', '),
                      icon: 'location-outline',
                    };
                  });
                } catch {
                  return [];
                }
              }}
              onSelectSuggestion={(s) => {
                setAddress(s.value);
                // If location is empty, try to populate it from the selected address (best-effort).
                if (!location.trim()) {
                  const parts = s.value.split(',').map(p => p.trim()).filter(Boolean);
                  if (parts.length >= 2) {
                    setLocation(parts.slice(-2).join(', '));
                  }
                }
              }}
            />

            <AutocompleteInput
              label="City (optional)"
              value={location}
              onChangeText={setLocation}
              placeholder="City, Country"
              suggestions={getLocationSuggestions}
              minQueryLength={1}
              showSuggestionsOnFocus={true}
            />
            <Caption color={theme.colors.textSecondary} style={{ marginTop: -4 }}>
              City or region only (e.g. Toronto, ON)—not the venue or floor. We merge this with Address
              when the street line is short or ambiguous; full addresses geocode on their own.
            </Caption>

            <SelectField
              label="Status"
              value={status}
              options={STATUS_OPTIONS}
              onValueChange={(value) => setStatus(value as ItineraryItem['status'])}
              avoidNestedModal
            />

            {/* Attachments */}
            <View 
              accessibilityRole="none"
              accessibilityLabel="Attachments"
              style={{ gap: theme.spacing(1) }}
            >
              <ThemedText variant="bodyStrong">Attachments</ThemedText>
              <Button
                label={uploading ? 'Uploading...' : 'Add Attachments'}
                variant="secondary"
                onPress={handleAddAttachments}
                disabled={uploading}
                accessibilityLabel={uploading ? 'Uploading attachments, please wait' : 'Add attachments'}
                accessibilityState={{ disabled: uploading, busy: uploading }}
                icon={<Ionicons name="attach-outline" size={16} color={theme.colors.primary} />}
              />
              {attachments.length > 0 && (
                <View style={{ gap: theme.spacing(0.75) }}>
                  {attachments.map((att, index) => {
                    const fileName = att.name || att.url.split('/').pop()?.split('?')[0] || `Attachment ${index + 1}`;
                    const isPDF = (att.contentType || fileName).toLowerCase().includes('pdf');
                    const isImage = (att.contentType || fileName).toLowerCase().match(/jpg|jpeg|png|gif/);
                    return (
                      <View
                        key={att.id || index}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: theme.spacing(1),
                          backgroundColor: theme.colors.borderMuted + '40',
                          borderRadius: theme.radii.md,
                          gap: theme.spacing(1),
                        }}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1 }}>
                          <Ionicons
                            name={isPDF ? 'document-text-outline' : isImage ? 'image-outline' : 'document-attach-outline'}
                            size={18}
                            color={theme.colors.primary}
                          />
                          <View style={{ flex: 1 }}>
                            <ThemedText variant="body" style={{ fontSize: 13 }}>
                              {fileName}
                            </ThemedText>
                            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                              {att.contentType || (isImage ? 'Image' : isPDF ? 'PDF' : 'Attachment')}
                            </Caption>
                          </View>
                        </View>
                        <Pressable 
                          accessibilityRole="button"
                          accessibilityLabel={`Remove attachment ${fileName}`}
                          accessibilityHint="Removes this attachment from the list"
                          onPress={() => handleRemoveAttachment(index)}
                        >
                          <Ionicons name="close-circle" size={20} color={theme.colors.error} />
                        </Pressable>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>

            {/* Actions */}
            <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(1) }}>
              <Button
                label="Cancel"
                variant="ghost"
                onPress={handleClose}
                accessibilityLabel="Cancel and close form"
                accessibilityHint="Discards any changes and closes the form"
                style={{ flex: 1 }}
              />
              <Button
                label={submitting ? 'Saving...' : initialItem ? 'Update' : 'Add'}
                onPress={handleSubmit}
                disabled={submitting || uploading}
                accessibilityLabel={submitting 
                  ? (initialItem ? 'Updating item, please wait' : 'Adding item, please wait')
                  : (initialItem ? 'Update itinerary item' : 'Add itinerary item')
                }
                accessibilityState={{ disabled: submitting || uploading, busy: submitting || uploading }}
                style={{ flex: 1 }}
              />
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>

      {/* Date Pickers — inside flex root so iOS in-modal overlay covers the sheet */}
      <Calendar
        visible={showStartDatePicker}
        onClose={() => setShowStartDatePicker(false)}
        onSelectDate={(date) => {
          setStartDate(date);
          setShowStartDatePicker(false);
        }}
        selectedDate={startDate}
        minDate={safeTripStartDate}
        maxDate={safeTripEndDate}
        title="Select Start Date"
      />

      <Calendar
        visible={showEndDatePicker}
        onClose={() => setShowEndDatePicker(false)}
        onSelectDate={(date) => {
          setEndDate(date);
          setShowEndDatePicker(false);
        }}
        selectedDate={endDate || undefined}
        minDate={safeTripStartDate}
        maxDate={safeTripEndDate}
        title="Select End Date"
      />
      </View>
      </SelectFieldModalProvider>
    </Modal>
  );
}

